import {DetailedError, parseResponse} from 'hono/client';
import {err, ok} from 'framework';
import type {Result} from 'framework';
import type {RpcClient} from '@/infrastructure/rpc/rpc-client';
import type {DailyDashboard} from '../../domain/daily-dashboard';
import type {
  CopyDayInput,
  CopyMealInput,
  CreateManualMealInput,
  DeleteMealInput,
  DashboardRepository,
  EditMealInput,
  FetchDailyDashboardInput,
  GetMealDetailInput,
} from '../../application/ports/dashboard.repository';
import type {MealDetail} from '../../domain/meal-detail';
import type {DashboardError} from '../../domain/errors';
import {
  InvalidMealInputError,
  MealNotFoundError,
  NetworkError,
  ServerError,
  UnauthorizedError,
} from '../../domain/errors';
import type {DailyDashboardDtoToDailyDashboardMapper} from '../mappers/daily-dashboard-dto-to-daily-dashboard.mapper';
import type {MealDetailDtoToMealDetailMapper} from '../mappers/meal-detail-dto-to-meal-detail.mapper';

type EditMealJson = Parameters<RpcClient['meals'][':mealId']['$patch']>[0]['json'];
type CreateManualMealJson = Parameters<RpcClient['meals']['$post']>[0]['json'];
type CopyMealJson = Parameters<RpcClient['meals'][':mealId']['copy']['$post']>[0]['json'];
type CopyDayJson = Parameters<RpcClient['meals']['copy-day']['$post']>[0]['json'];

const mealActionStatusErrorMap: Record<number, () => DashboardError> = {
  401: () => new UnauthorizedError(),
  404: () => new MealNotFoundError(),
  422: () => new InvalidMealInputError(),
};

/**
 * Fetches the daily dashboard via the typed Hono RPC client and maps the
 * response into the domain aggregate. HTTP failure status codes are
 * folded into typed `DashboardError`s; thrown errors (offline, DNS, etc.)
 * become `NetworkError`. The `Result` API keeps the loader free of
 * try/catch and lets the route loader switch on the error class.
 */
export class RpcDashboardRepository implements DashboardRepository {
  constructor(
    private readonly rpcClient: RpcClient,
    private readonly dailyDashboardMapper: DailyDashboardDtoToDailyDashboardMapper,
    private readonly mealDetailMapper: MealDetailDtoToMealDetailMapper,
  ) {}

  async fetchDaily(
    input: FetchDailyDashboardInput,
  ): Promise<Result<DailyDashboard, DashboardError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.dashboard.daily.$get({
          query: {date: input.date ?? undefined},
        }),
      );

      return ok(this.dailyDashboardMapper.transform(dto));
    } catch (cause) {
      if (cause instanceof DetailedError) {
        if (cause.statusCode === 401) {
          return err(new UnauthorizedError());
        }

        return err(new ServerError());
      }

      return err(new NetworkError());
    }
  }

  async getMealDetail(input: GetMealDetailInput): Promise<Result<MealDetail, DashboardError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.meals[':mealId'].$get({
          param: {mealId: input.mealId},
        }),
      );

      return ok(this.mealDetailMapper.transform(dto));
    } catch (cause) {
      return this.mapMealActionError(cause);
    }
  }

  async deleteMeal(input: DeleteMealInput): Promise<Result<void, DashboardError>> {
    try {
      await parseResponse(
        this.rpcClient.meals[':mealId'].$delete({
          param: {mealId: input.mealId},
        }),
      );

      return ok(undefined);
    } catch (cause) {
      return this.mapMealActionError(cause);
    }
  }

  async editMeal(input: EditMealInput): Promise<Result<MealDetail, DashboardError>> {
    try {
      const json: EditMealJson = {
        mealType: input.command.mealType,
        consumedAt: input.command.consumedAt,
        entries: input.command.entries.map(entry => ({
          displayName: entry.displayName,
          sourceFoodId: null,
          source: entry.source,
          quantity: entry.quantity,
          unit: entry.unit,
          gramEquivalent: entry.gramEquivalent,
          nutritionPer100g: entry.nutritionPer100g,
          kcal: entry.kcal,
          proteinGrams: entry.proteinGrams,
          carbsGrams: entry.carbsGrams,
          fatGrams: entry.fatGrams,
          notes: entry.notes,
        })),
      };
      const dto = await parseResponse(
        this.rpcClient.meals[':mealId'].$patch({
          param: {mealId: input.mealId},
          json,
        }),
      );

      return ok(this.mealDetailMapper.transform(dto));
    } catch (cause) {
      return this.mapMealActionError(cause);
    }
  }

  async createManualMeal(
    input: CreateManualMealInput,
  ): Promise<Result<MealDetail, DashboardError>> {
    try {
      const json: CreateManualMealJson = {
        mealType: input.command.mealType,
        nutritionDate: input.command.nutritionDate,
        consumedAt: input.command.consumedAt,
        entries: input.command.entries.map(entry => ({
          displayName: entry.displayName,
          quantity: entry.quantity,
          unit: entry.unit,
          gramEquivalent: entry.gramEquivalent,
          kcal: entry.kcal,
          proteinGrams: entry.proteinGrams,
          carbsGrams: entry.carbsGrams,
          fatGrams: entry.fatGrams,
          notes: entry.notes,
          source: entry.source,
          nutritionPer100g: entry.nutritionPer100g,
          origin: entry.origin,
        })),
      };
      const dto = await parseResponse(this.rpcClient.meals.$post({json}));

      return ok(this.mealDetailMapper.transform(dto));
    } catch (cause) {
      return this.mapMealActionError(cause);
    }
  }

  async copyMeal(input: CopyMealInput): Promise<Result<MealDetail, DashboardError>> {
    try {
      const json: CopyMealJson = {
        mealType: input.command.mealType,
        nutritionDate: input.command.nutritionDate,
        consumedAt: input.command.consumedAt,
      };
      const dto = await parseResponse(
        this.rpcClient.meals[':mealId'].copy.$post({
          param: {mealId: input.mealId},
          json,
        }),
      );

      return ok(this.mealDetailMapper.transform(dto));
    } catch (cause) {
      return this.mapMealActionError(cause);
    }
  }

  async copyDay(input: CopyDayInput): Promise<Result<readonly MealDetail[], DashboardError>> {
    try {
      const json: CopyDayJson = {
        sourceDate: input.command.sourceDate,
        targetDate: input.command.targetDate,
      };
      const envelope = await parseResponse(this.rpcClient.meals['copy-day'].$post({json}));

      return ok(envelope.data.map(dto => this.mealDetailMapper.transform(dto)));
    } catch (cause) {
      return this.mapMealActionError(cause);
    }
  }

  private mapMealActionError(cause: unknown): Result<never, DashboardError> {
    if (cause instanceof DetailedError) {
      const createError = mealActionStatusErrorMap[cause.statusCode];

      return err(createError === undefined ? new ServerError() : createError());
    }

    return err(new NetworkError());
  }
}
